/*
	AWSIssuesApi.ts -- AWS-specific interface to making Issues related network calls.
*/
import { HISTORY, ISSUES } from 'Config/Paths';
import { CreateIssueRequest, IssueHistoryResponse, IssueResponse, UpdateIssueRequest } from 'Models/Issues';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';
import { restApiDelete, restApiGet, restApiPut } from 'RestApiConnector';

import { IssuesApi } from './IssuesApi';

export class AWSIssuesApi implements IssuesApi {
    /**
     * @param module The due diligence module whose issues `getAllDueDiligenceIssues` returns; no other method uses it. Defaults to TPRM; `App.tsx` shares one default instance between Controls and TPRM pages, and creates a separate instance for UCRM.
     */
    constructor(private module: DueDiligenceModule = ProductModule.TPRM) {}

    getAllIssues = (): Promise<ResponseModel<IssueResponse[]>> => {
        return restApiGet(`/${ISSUES}`, {});
    };
    getAllControlIssues = (): Promise<ResponseModel<IssueResponse[]>> => {
        return restApiGet(`/${ISSUES}`, { issue_type: ProductModule.CONTROLS });
    };
    getAllDueDiligenceIssues = (): Promise<ResponseModel<IssueResponse[]>> => {
        return restApiGet(`/${ISSUES}`, { issue_type: this.module });
    };
    getIssuesByControl = (controlIdentifier: string): Promise<ResponseModel<IssueResponse[]>> => {
        return restApiGet(`/${ISSUES}`, { control: controlIdentifier });
    };
    getIssuesByOrganization = (organizationId: string): Promise<ResponseModel<IssueResponse[]>> => {
        return restApiGet(`/${ISSUES}`, { organization_id: organizationId });
    };
    getIssue = (id: string): Promise<ResponseModel<IssueResponse>> => {
        return restApiGet(`/${ISSUES}/${id}`, {});
    };
    getIssueHistory = (issueId: string): Promise<ResponseModel<IssueHistoryResponse[]>> => {
        return restApiGet(`/${ISSUES}/${HISTORY}/${issueId}`, {});
    };
    createIssue = (createIssueRequest: CreateIssueRequest): Promise<ResponseModel<IssueResponse>> => {
        return restApiPut(`/${ISSUES}`, createIssueRequest);
    };
    updateIssue = (id: string, updateIssueRequest: UpdateIssueRequest): Promise<ResponseModel<IssueResponse>> => {
        return restApiPut(`/${ISSUES}/${id}`, updateIssueRequest);
    };
    deleteIssue = (id: string): Promise<ResponseModel<void>> => {
        return restApiDelete(`/${ISSUES}/${id}`);
    };
}
